import { Navigate, Route, Routes } from 'react-router-dom';
import AdminShell from './layout/AdminShell';
import PublicShell from './layout/PublicShell';
import Catalog from './pages/Catalog';
import CoursePage from './pages/CoursePage';
import Home from './pages/Home';
import InstructorPage from './pages/InstructorPage';
import CartPage from './pages/CartPage';
import CheckoutResult from './pages/CheckoutResult';
import Invoice from './pages/Invoice';
import Login from './pages/Login';
import Messages from './pages/Messages';
import MentorCalendar from './pages/MentorCalendar';
import MyCourses from './pages/MyCourses';
import MyPurchases from './pages/MyPurchases';
import MyPoints from './pages/MyPoints';
import PolicyPage from './pages/PolicyPage';
import SsoLanding from './pages/SsoLanding';
import Wishlist from './pages/Wishlist';
import ApplyPage from './pages/instructor/ApplyPage';
import ApplicationsPage from './pages/admin/ApplicationsPage';
import CategoriesPage from './pages/admin/CategoriesPage';
import EarningsPage from './pages/admin/EarningsPage';
import PayoutsPage from './pages/admin/PayoutsPage';
import CourseEditor from './pages/admin/CourseEditor';
import CourseStudentDetail from './pages/admin/CourseStudentDetail';
import CoursesPage from './pages/admin/CoursesPage';
import CouponsPage from './pages/admin/CouponsPage';
import EnrollmentsPage from './pages/admin/EnrollmentsPage';
import PaymentsPage from './pages/admin/PaymentsPage';
import SettingsPage from './pages/admin/SettingsPage';
import PlayerPage from './pages/learn/PlayerPage';
import { getUser, isLoggedIn } from './lib/auth';

const isStaff = () => ['admin', 'manager'].includes((getUser() || {}).lr);
const isAuthor = () => ['admin', 'manager', 'instructor'].includes((getUser() || {}).lr);

function RequireAuth({ children }) {
  if (!isLoggedIn()) return <Navigate to="/login" replace />;
  return children;
}

// The back-office shell admits staff AND instructors (Phase 5); the backend
// scopes instructors to their own courses.
function RequireAuthor({ children }) {
  if (!isLoggedIn()) return <Navigate to="/login" replace />;
  if (!isAuthor()) return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }) {
  if ((getUser() || {}).lr !== 'admin') return <Navigate to="/admin/courses" replace />;
  return children;
}

function StaffOnly({ children }) {
  if (!isStaff()) return <Navigate to="/admin/courses" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/sso-login" element={<SsoLanding />} />
      <Route path="/sso" element={<SsoLanding />} />
      {/* Storefront — PRIVATE. Every page requires a signed-in session; an
          anonymous visitor hitting the domain is bounced to /login, exactly
          like the CMS /my-account. (Was world-readable in Phase 2.) */}
      <Route element={<RequireAuth><PublicShell /></RequireAuth>}>
        <Route index element={<Home />} />
        <Route path="courses" element={<Catalog />} />
        <Route path="course/:slug" element={<CoursePage />} />
        <Route path="instructor/:memberId" element={<InstructorPage />} />
        <Route path="p/:pageKey" element={<PolicyPage />} />
        <Route
          path="my/courses"
          element={
            <RequireAuth>
              <MyCourses />
            </RequireAuth>
          }
        />
        <Route path="my/wishlist" element={<RequireAuth><Wishlist /></RequireAuth>} />
        <Route path="my/messages" element={<RequireAuth><Messages /></RequireAuth>} />
        <Route path="my/mentor" element={<RequireAuth><MentorCalendar /></RequireAuth>} />
        <Route path="my/purchases" element={<RequireAuth><MyPurchases /></RequireAuth>} />
        <Route path="my/points" element={<RequireAuth><MyPoints /></RequireAuth>} />
        <Route path="my/invoices/:paymentId" element={<RequireAuth><Invoice /></RequireAuth>} />
        <Route path="cart" element={<RequireAuth><CartPage /></RequireAuth>} />
        <Route path="checkout/success" element={<RequireAuth><CheckoutResult /></RequireAuth>} />
        <Route path="instructor/apply" element={<RequireAuth><ApplyPage /></RequireAuth>} />
      </Route>
      {/* Player — its own full-screen layout (curriculum sidebar) */}
      <Route
        path="/learn/:slug"
        element={
          <RequireAuth>
            <PlayerPage />
          </RequireAuth>
        }
      />
      {/* Back-office — staff only */}
      <Route
        path="/admin"
        element={
          <RequireAuthor>
            <AdminShell />
          </RequireAuthor>
        }
      >
        <Route index element={<Navigate to="/admin/courses" replace />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="courses/:courseId" element={<CourseEditor />} />
        <Route path="courses/:courseId/students/:memberId" element={<AdminOnly><CourseStudentDetail /></AdminOnly>} />
        <Route path="earnings" element={<EarningsPage />} />
        <Route path="categories" element={<StaffOnly><CategoriesPage /></StaffOnly>} />
        <Route path="enrollments" element={<StaffOnly><EnrollmentsPage /></StaffOnly>} />
        <Route path="payments" element={<StaffOnly><PaymentsPage /></StaffOnly>} />
        <Route path="coupons" element={<StaffOnly><CouponsPage /></StaffOnly>} />
        <Route path="applications" element={<StaffOnly><ApplicationsPage /></StaffOnly>} />
        <Route path="payouts" element={<StaffOnly><PayoutsPage /></StaffOnly>} />
        <Route path="settings" element={<StaffOnly><SettingsPage /></StaffOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
